import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { InputPanel } from './InputPanel';
import { ProgressTracker } from './ProgressTracker';
import { FilterPanel } from './FilterPanel';
import { SimpleResultsTable } from './visualizations/SimpleResultsTable';
import { DomainTreeMap } from './visualizations/DomainTreeMap';
import { MatrixView } from './visualizations/MatrixView';
import { Download, History, Table, TreePine, Grid3x3, FolderOpen } from 'lucide-react';
import { ProviderId, DomainHit, QueryProgress, FilterState, RunMetadata, Regime } from '@/lib/trustHub/v1/types';
import { PROVIDERS, getProviderById } from '@/lib/trustHub/v1/providers';
import { aggregateResultsMultiProvider } from '@/lib/trustHub/v1/aggregate';
import { applyFilters } from '@/lib/trustHub/v1/filters';
import { saveToLocalStorage, loadFromLocalStorage, clearLocalStorage } from '@/lib/trustHub/v1/storage';
import { detectCountryFromQuery } from '@/lib/trustHub/v1/countries';
import { RunHistoryV1 } from './RunHistoryV1';
import Papa from 'papaparse';

const DEFAULT_QUERIES = `Best CRM for real estate
HIPAA compliant email for therapists
Zapier alternatives for property management`;

export const TrustHubMapperV1: React.FC = () => {
  // State
  const [queries, setQueries] = useState(DEFAULT_QUERIES);
  const [selectedProviders, setSelectedProviders] = useState<Set<ProviderId>>(new Set(['openai', 'claude']));
  const [country, setCountry] = useState('auto');
  const [useCache, setUseCache] = useState(true);
  const [saveSnapshot, setSaveSnapshot] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [progress, setProgress] = useState<QueryProgress[]>([]);
  const [progressCollapsed, setProgressCollapsed] = useState(false);
  const [domainMap, setDomainMap] = useState<Map<string, DomainHit>>(new Map());
  const [metadata, setMetadata] = useState<RunMetadata | null>(null);
  const [unlockPassword, setUnlockPassword] = useState('');
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [regime, setRegime] = useState<Regime>('r2');
  const [filters, setFilters] = useState<FilterState>({
    providers: new Set(['openai', 'claude']),
    exclusiveMode: false,
    minCoverage: 0,
    accessFilter: 'all',
    tldFilters: { gov: false, edu: false, org: false },
    focusedQuery: null,
    consideredWeight: 0.25,
    sortBy: 'used',
    sortDir: 'desc',
    viewMode: 'table',
  });
  const [viewMode, setViewMode] = useState<'table' | 'treemap' | 'matrix'>('table');
  const [showHistory, setShowHistory] = useState(false);

  const abortControllerRef = useRef<AbortController | null>(null);

  // Load from localStorage on mount
  useEffect(() => {
    const stored = loadFromLocalStorage();
    if (stored) {
      setDomainMap(stored.domainMap);
      setMetadata(stored.metadata);
      setFilters(stored.filters);
      setQueries(stored.metadata.queries.join('\n'));
      setSelectedProviders(new Set(stored.metadata.providers));
      setCountry(stored.metadata.country);
      toast.success('Loaded previous results from cache');
    }
  }, []);

  // Save to localStorage when results change
  useEffect(() => {
    if (domainMap.size > 0 && metadata) {
      saveToLocalStorage(domainMap, metadata, filters);
    }
  }, [domainMap, metadata, filters]);

  // Derived data
  const queryLines = useMemo(() => {
    return queries.split('\n').filter(q => q.trim());
  }, [queries]);

  const filteredDomains = useMemo(() => {
    return applyFilters(domainMap, filters, queryLines.length);
  }, [domainMap, filters, queryLines.length]);

  // Provider toggle
  const handleProviderToggle = (provider: ProviderId) => {
    const newSet = new Set(selectedProviders);
    if (newSet.has(provider)) {
      newSet.delete(provider);
    } else {
      newSet.add(provider);
    }
    setSelectedProviders(newSet);
  };

  // Sync access list
  const syncAccessList = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('import-access-csv');
      if (error) throw error;
      console.log('Access list synced:', data);
    } catch (error) {
      console.error('Failed to sync access list:', error);
      // Non-blocking - continue with analysis
    }
  };

  // Apply access list data to domain map
  const enrichWithAccessData = async (domains: Map<string, DomainHit>) => {
    try {
      const { data, error } = await supabase
        .from('access_list')
        .select('domain, has_access, tag');

      if (error) throw error;

      data?.forEach((entry: any) => {
        const hit = domains.get(entry.domain);
        if (hit) {
          hit.hasAccess = entry.has_access || false;
          hit.tag = entry.tag || 'unknown';
        }
      });
    } catch (error) {
      console.error('Failed to enrich with access data:', error);
    }
  };

  // Validate password
  const validatePassword = async (password: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase.functions.invoke('validate-grow-geo-password', {
        body: { password }
      });
      if (error) throw error;
      return data?.valid === true;
    } catch (error) {
      console.error('Password validation error:', error);
      return false;
    }
  };

  // Handle password change with validation
  const handlePasswordChange = async (password: string) => {
    setUnlockPassword(password);
    if (password.length >= 4) {
      const valid = await validatePassword(password);
      setIsUnlocked(valid);
      if (valid) {
        toast.success('Extended mode unlocked!');
      }
    } else {
      setIsUnlocked(false);
    }
  };

  // Run analysis
  const runAnalysis = async () => {
    const baseLimit = 100;
    const extendedLimit = 1000;
    const maxQueries = isUnlocked ? extendedLimit : baseLimit;

    if (queryLines.length === 0 || selectedProviders.size === 0) {
      toast.error('Please enter queries and select at least one provider');
      return;
    }

    if (queryLines.length > maxQueries) {
      toast.error(`Please reduce queries to ${maxQueries} or less${!isUnlocked ? ', or unlock extended mode' : ''}`);
      return;
    }

    // Sync access list first
    await syncAccessList();

    setIsProcessing(true);
    setIsPaused(false);
    abortControllerRef.current = new AbortController();

    const startedAt = new Date().toISOString();

    // Initialize progress
    const initialProgress: QueryProgress[] = [];
    queryLines.forEach((query) => {
      Array.from(selectedProviders).forEach((provider) => {
        initialProgress.push({
          query,
          provider,
          status: 'pending',
        });
      });
    });
    setProgress(initialProgress);

    // Detect country
    const detectedCountry = country === 'auto' 
      ? queryLines.some(q => detectCountryFromQuery(q) === 'IL') ? 'IL' : 'auto'
      : country;

    const results: Array<{
      query: string;
      provider: ProviderId;
      response: any;
    }> = [];

    // Process queries with dynamic limit
    const limitedQueries = queryLines.slice(0, maxQueries);
    
    // Fire all queries for both providers in parallel - no batching, no delays
    const allCombinations = limitedQueries.flatMap((query) =>
      Array.from(selectedProviders).map((providerId) => ({ query, providerId }))
    );

    // Fire all requests simultaneously
    const allPromises = allCombinations.map(async ({ query, providerId }) => {
      const progressIndex = initialProgress.findIndex(
        p => p.query === query && p.provider === providerId
      );

      try {
        setProgress(prev => prev.map((p, i) => 
          i === progressIndex ? { ...p, status: 'processing' } : p
        ));

        const provider = getProviderById(providerId);
        if (!provider) throw new Error(`Provider ${providerId} not found`);

        const response = await provider.run(query, detectedCountry, regime);

        results.push({
          query,
          provider: providerId,
          response,
        });

        setProgress(prev => prev.map((p, i) => 
          i === progressIndex ? { ...p, status: 'done' } : p
        ));
      } catch (error) {
        console.error(`Error for ${query} (${providerId}):`, error);
        
        // Extract detailed error message
        let errorMessage = 'Unknown error';
        if (error instanceof Error) {
          errorMessage = error.message;
        }
        
        setProgress(prev => prev.map((p, i) => 
          i === progressIndex ? {
            ...p,
            status: 'error',
            error: errorMessage
          } : p
        ));
      }
    });

    // Wait for all requests to complete
    await Promise.all(allPromises);

    

    if (results.length === 0) {
      toast.error('All queries failed');
      setIsProcessing(false);
      return;
    }

    // Aggregate results
    const aggregated = aggregateResultsMultiProvider(results, queryLines);

    // Enrich with access data
    await enrichWithAccessData(aggregated);

    setDomainMap(aggregated);
    setMetadata({
      queries: queryLines,
      providers: Array.from(selectedProviders),
      country: detectedCountry,
      startedAt,
      completedAt: new Date().toISOString(),
    });

    setIsProcessing(false);
    toast.success(`Analysis complete! Found ${aggregated.size} unique domains.`);

    // Save snapshot if requested
    if (saveSnapshot) {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        // Convert domain map to serializable format
        const domainsArray = Array.from(aggregated.values()).map((hit) => ({
          domain: hit.domain,
          usedBy: Array.from(hit.usedBy),
          consideredBy: Array.from(hit.consideredBy),
          usedUrls: hit.usedUrls,
          consideredUrls: hit.consideredUrls,
          providers: Array.from(hit.providers),
          providerBreakdown: hit.providerBreakdown,
          queryBreakdown: hit.queryBreakdown ? Object.fromEntries(
            Object.entries(hit.queryBreakdown).map(([q, qb]) => [
              q,
              {
                usedUrls: qb.usedUrls,
                consideredUrls: qb.consideredUrls,
                providers: Array.from(qb.providers),
                providerByUrl: Object.fromEntries(
                  Object.entries(qb.providerByUrl).map(([url, providers]) => [
                    url,
                    Array.from(providers)
                  ])
                )
              }
            ])
          ) : undefined,
          favicon: hit.favicon,
          hasAccess: hit.hasAccess,
          tag: hit.tag,
        }));

        const { error: saveError } = await supabase
          .from('trust_hub_runs_v1')
          .insert({
            user_id: user?.id || null,
            queries: queryLines,
            providers: Array.from(selectedProviders),
            country: detectedCountry,
            domains: domainsArray,
            metadata: {
              queries: queryLines,
              providers: Array.from(selectedProviders),
              country: detectedCountry,
              startedAt,
              completedAt: new Date().toISOString(),
            },
            filters: {
              ...filters,
              providers: Array.from(filters.providers),
            },
          });
        
        if (saveError) throw saveError;
        toast.success('Snapshot saved to history!');
      } catch (error) {
        console.error('Error saving snapshot:', error);
        toast.error('Failed to save snapshot to history');
      }
    }
  };

  // Pause/Resume
  const handlePause = () => {
    setIsPaused(!isPaused);
    toast.info(isPaused ? 'Resumed' : 'Paused');
  };

  // Stop
  const handleStop = () => {
    abortControllerRef.current?.abort();
    setIsProcessing(false);
    setIsPaused(false);
    toast.info('Analysis stopped');
  };

  // Export CSV
  const exportCSV = () => {
    const rows = filteredDomains.map((hit) => ({
      domain: hit.domain,
      used_count: hit.usedBy.size,
      considered_count: hit.consideredBy.size,
      providers: Array.from(hit.providers).join(';'),
      coverage_percent: ((hit.usedBy.size / queryLines.length) * 100).toFixed(1),
      access: hit.tag || 'unknown',
      sample_urls: [...new Set([...hit.usedUrls, ...hit.consideredUrls])].slice(0, 3).join(';'),
    }));

    const csv = Papa.unparse(rows);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `growgeo-analysis-${new Date().toISOString()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exported');
  };

  // Export JSON
  const exportJSON = () => {
    const data = {
      metadata,
      domains: Array.from(domainMap.values()).map((hit) => ({
        domain: hit.domain,
        usedBy: Array.from(hit.usedBy),
        consideredBy: Array.from(hit.consideredBy),
        usedUrls: hit.usedUrls,
        consideredUrls: hit.consideredUrls,
        providers: Array.from(hit.providers),
        providerBreakdown: hit.providerBreakdown,
        tag: hit.tag,
      })),
      filters,
    };

    const json = JSON.stringify(data, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `growgeo-analysis-${new Date().toISOString()}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('JSON exported');
  };

  // Clear results
  const handleClearResults = () => {
    setDomainMap(new Map());
    setMetadata(null);
    setProgress([]);
    clearLocalStorage();
    toast.success('Results cleared');
  };

  // Load run from history
  const handleLoadRun = (run: any) => {
    // Reconstruct domain map from serialized data
    const reconstructedMap = new Map<string, DomainHit>();
    
    run.domains.forEach((domain: any) => {
      reconstructedMap.set(domain.domain, {
        domain: domain.domain,
        usedBy: new Set(domain.usedBy),
        consideredBy: new Set(domain.consideredBy),
        usedUrls: domain.usedUrls,
        consideredUrls: domain.consideredUrls,
        providers: new Set(domain.providers),
        providerBreakdown: domain.providerBreakdown,
        queryBreakdown: domain.queryBreakdown ? Object.fromEntries(
          Object.entries(domain.queryBreakdown).map(([q, qb]: [string, any]) => [
            q,
            {
              usedUrls: qb.usedUrls,
              consideredUrls: qb.consideredUrls,
              providers: new Set(qb.providers),
              providerByUrl: Object.fromEntries(
                Object.entries(qb.providerByUrl || {}).map(([url, providers]: [string, any]) => [
                  url,
                  new Set(providers)
                ])
              )
            }
          ])
        ) : undefined,
        favicon: domain.favicon,
        hasAccess: domain.hasAccess,
        tag: domain.tag,
      });
    });

    setDomainMap(reconstructedMap);
    setMetadata(run.metadata);
    setQueries(run.queries.join('\n'));
    setSelectedProviders(new Set(run.providers));
    setCountry(run.country);
    setFilters({
      ...run.filters,
      providers: new Set(run.filters.providers),
    });
    setShowHistory(false);
    toast.success('Snapshot loaded from history');
  };

  return (
    <div className="space-y-6">
      {/* History Toggle */}
      <div className="flex justify-end">
        <Button
          variant="outline"
          onClick={() => setShowHistory(!showHistory)}
        >
          <FolderOpen className="h-4 w-4 mr-2" />
          {showHistory ? 'Hide History' : 'Show History'}
        </Button>
      </div>

      {/* History Panel */}
      {showHistory && <RunHistoryV1 onLoadRun={handleLoadRun} />}

      {/* Input Panel */}
      <Card className="p-6">
        <InputPanel
          queries={queries}
          onQueriesChange={setQueries}
          selectedProviders={selectedProviders}
          onProviderToggle={handleProviderToggle}
          country={country}
          onCountryChange={setCountry}
          useCache={useCache}
          onUseCacheChange={setUseCache}
          saveSnapshot={saveSnapshot}
          onSaveSnapshotChange={setSaveSnapshot}
          isProcessing={isProcessing}
          isPaused={isPaused}
          unlockPassword={unlockPassword}
          onUnlockPasswordChange={handlePasswordChange}
          isUnlocked={isUnlocked}
          regime={regime}
          onRegimeChange={setRegime}
          onRun={runAnalysis}
          onPause={handlePause}
          onStop={handleStop}
        />
      </Card>

      {/* Progress Tracker */}
      {progress.length > 0 && (
        <ProgressTracker
          progress={progress}
          isCollapsed={progressCollapsed}
          onToggleCollapse={() => setProgressCollapsed(!progressCollapsed)}
        />
      )}

      {/* Results */}
      {domainMap.size > 0 && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <div className="lg:col-span-1">
              <FilterPanel
                filters={filters}
                onFiltersChange={setFilters}
                queries={queryLines}
              />
            </div>

            <div className="lg:col-span-3 space-y-6">
              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="p-4">
                  <div className="text-2xl font-bold">{filteredDomains.length}</div>
                  <div className="text-sm text-muted-foreground">Domains</div>
                </Card>
                <Card className="p-4">
                  <div className="text-2xl font-bold">{queryLines.length}</div>
                  <div className="text-sm text-muted-foreground">Queries</div>
                </Card>
                <Card className="p-4">
                  <div className="text-2xl font-bold">{selectedProviders.size}</div>
                  <div className="text-sm text-muted-foreground">Providers</div>
                </Card>
                <Card className="p-4">
                  <div className="text-2xl font-bold">
                    {metadata?.country || 'auto'}
                  </div>
                  <div className="text-sm text-muted-foreground">Country</div>
                </Card>
              </div>

              {/* Visualization */}
              <Card className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold">
                    Results ({filteredDomains.length} domains)
                  </h3>
                  <div className="flex gap-2">
                    {/* View Toggle */}
                    <div className="flex gap-1 mr-2">
                      <Button
                        variant={viewMode === 'table' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setViewMode('table')}
                      >
                        <Table className="h-4 w-4 mr-1" />
                        Table
                      </Button>
                      <Button
                        variant={viewMode === 'treemap' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setViewMode('treemap')}
                      >
                        <TreePine className="h-4 w-4 mr-1" />
                        Treemap
                      </Button>
                      <Button
                        variant={viewMode === 'matrix' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setViewMode('matrix')}
                      >
                        <Grid3x3 className="h-4 w-4 mr-1" />
                        Matrix
                      </Button>
                    </div>
                    
                    <Button variant="outline" size="sm" onClick={exportCSV}>
                      <Download className="h-4 w-4 mr-1" />
                      CSV
                    </Button>
                    <Button variant="outline" size="sm" onClick={exportJSON}>
                      <Download className="h-4 w-4 mr-1" />
                      JSON
                    </Button>
                    <Button variant="outline" size="sm" onClick={handleClearResults}>
                      <History className="h-4 w-4 mr-1" />
                      Clear
                    </Button>
                  </div>
                </div>

                {viewMode === 'table' ? (
                  <SimpleResultsTable
                    domains={filteredDomains}
                    totalQueries={queryLines.length}
                  />
                ) : viewMode === 'treemap' ? (
                  <DomainTreeMap
                    domains={filteredDomains}
                    queries={queryLines}
                    totalQueries={queryLines.length}
                    maxDomains={50}
                    consideredWeight={filters.consideredWeight}
                    onQueryClick={(query) => {
                      // Toggle behavior: if already focused on this query, clear it; otherwise set it
                      setFilters({ 
                        ...filters, 
                        focusedQuery: filters.focusedQuery === query ? null : query 
                      });
                    }}
                  />
                ) : (
                  <MatrixView
                    domains={filteredDomains}
                    queries={queryLines}
                    maxDomains={30}
                    onFocusQuery={(query) => {
                      // Toggle behavior: if already focused on this query, clear it; otherwise set it
                      setFilters({ 
                        ...filters, 
                        focusedQuery: filters.focusedQuery === query ? null : query 
                      });
                    }}
                    onFocusDomain={(domain) => {
                      toast.info(`Domain: ${domain}`);
                    }}
                  />
                )}
              </Card>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
